import { usePost } from "@albertine/api-client";
import {
    type AdditionalServiceLanguage,
    type ServiceLanguage,
    ServiceLanguageMap,
} from "@albertine/i18n";
import type { GooglePlace, OfferingCategory, Supplier } from "@albertine/types";
import { zodResolver } from "@hookform/resolvers/zod";
import * as Sentry from "@sentry/react";
import type React from "react";
import { useMemo, useRef, useState } from "react";
import { type SubmitErrorHandler, type SubmitHandler, useForm } from "react-hook-form";
import { type CatalogSourceKey, CatalogSources } from "../../common/CatalogCategories";
import {
    CatalogItemFormSchema,
    getCatalogItemKeyDetail,
    hasTranslations,
} from "../../common/CatalogItem";
import {
    CategoriesWithUncategorized,
    categoryDisplayName,
    OfferingCategoryUncategorizedKey,
} from "../../common/types/OfferingCategory";
import { removeDuplicates } from "../../common/utils/array.util";
import {
    fromCatalogItemFormToEditables,
    isRecentlyOpened,
    showAddress,
    showEventDates,
    showRecentlyOpened,
    showTravelItineraryNotes,
} from "../../common/utils/catalog.util";
import { newCatalogItemPayloadFromGooglePlace } from "../../common/utils/google/GooglePlaceService";
import { GooglePlaceFieldsNewCatalogItem } from "../../common/utils/google/place.util";
import { isEmpty } from "../../common/utils/string.util";
import { EventEmitter } from "../../EventEmitter";
import { scrollRefToTop } from "../../utils/window.util";
import AppPreview from "../Common/AppPreview/AppPreview";
import Button from "../Common/Buttons";
import Dropdown, { type DropdownOption } from "../Common/Dropdown";
import { Flex } from "../Common/Flex";
import { Form } from "../Common/Form/Form";
import Input from "../Common/Input";
import AutocompleteDropdown from "../Common/Input.AutocompleteDropdown";
import Label from "../Common/Label";
import Tag from "../Common/Tag";
import Toggle from "../Common/Toggle";
import GooglePlaceInputField from "../GooglePlaceInputField/GooglePlaceInputField";
import { TitleValuePair, TitleValuePairs } from "../MemberData/TitleValuePairs";
import {
    getUrlFromReference,
    type ImageReference,
    itemImagesToImageReferences,
    placeImagesToImageReferences,
} from "./ImageReference";
import ImageSelector from "./ImageSelector";
import SelectTagsWithCategory from "./SelectTagsWithCategory";
import { useSaveCatalogItem } from "./useSaveCatalogItem";
import "./CatalogItemForm.css";
import {
    type CatalogItemFull,
    type Tenant,
    TenantSchema,
} from "@albertine/types/openapi/lmt";
import { usePostHog } from "posthog-js/react";
import { toApiErrorMessage } from "@/common/utils/apiError.util";
import { formatTenantName } from "@/common/utils/tenant.util";
import {
    type CatalogItemEditableTranslation,
    catalogItemNameMaxLength,
} from "../../common/types/Translation";
import { collectErrorMessages } from "../../common/utils/form.util";
import type { MenuButtonOptions } from "../Common/Button/MenuButton";
import Icon from "../Common/Icon";
import { TiptapEditor } from "../Common/TiptapEditor";
import Tooltip from "../Common/Tooltip";
import TranslationTabs from "../Common/TranslationTabs";
import SelectTags, { type TagSelection } from "./SelectTags";

const offeringCategoryOptions = CategoriesWithUncategorized.options.map(
    (k: CategoriesWithUncategorized): DropdownOption => ({
        value: k,
        label: categoryDisplayName(k, "en"),
    }),
);

function CatalogItemForm({
    catalogItem,
    googlePlace,
    initialValues,
    isAdmin,
    suppliers: listOfSuppliers,
    defaultCatalogSource = "unknown",
    onCancel,
    onSave,
}: {
    catalogItem: CatalogItemFull | undefined;
    googlePlace: GooglePlace | undefined;
    initialValues?: Partial<CatalogItemFull>;
    isAdmin: boolean;
    suppliers: Supplier[];
    defaultCatalogSource?: CatalogSourceKey;
    onCancel: () => void;
    onSave: (editedItem: CatalogItemFull) => void;
}) {
    const googlePlacePayload = googlePlace
        ? {
              ...newCatalogItemPayloadFromGooglePlace(googlePlace),
              images:
                  googlePlace.photos?.map((item) => ({
                      id: item.name,
                      url: item.photoUri || "",
                  })) || [],
          }
        : undefined;
    const inheritedItem:
        | Partial<CatalogItemFull & { googlePlaceData: GooglePlace }>
        | undefined =
        catalogItem ??
        (initialValues || googlePlace
            ? {
                  ...initialValues,
                  ...googlePlacePayload,
                  offeringCategories: googlePlacePayload?.offeringCategories?.length
                      ? googlePlacePayload.offeringCategories
                      : initialValues?.offeringCategories,
              }
            : undefined);
    const inheritedItemKeyDetail = inheritedItem
        ? getCatalogItemKeyDetail(inheritedItem)
        : undefined;

    const defaultTranslationDe = inheritedItem?.translations?.de;
    const defaultTranslationFr = inheritedItem?.translations?.fr;
    const {
        getValues,
        setValue,
        handleSubmit,
        trigger,
        reset,
        formState: { errors, isSubmitted, isValid, defaultValues },
    } = useForm<CatalogItemFormSchema>({
        resolver: zodResolver(CatalogItemFormSchema),
        defaultValues: {
            name: inheritedItem?.name || undefined,
            description: inheritedItem?.description || undefined,
            images: inheritedItem?.images || [],
            offeringCategory: inheritedItem?.offeringCategories
                ? inheritedItem?.offeringCategories[0]
                : undefined,
            localInsights: inheritedItem?.localInsights || undefined,
            notes: inheritedItem?.notes || undefined,
            source: inheritedItem?.source
                ? (inheritedItem?.source as CatalogSourceKey)
                : defaultCatalogSource || "unknown",
            address: inheritedItem?.address || undefined,
            phoneNumber: inheritedItem?.phoneNumber || undefined,
            website: inheritedItem?.website || undefined,
            tags: inheritedItem?.tags || undefined,
            published: inheritedItem?.published || false,
            addressComponents: inheritedItem?.addressComponents || undefined,
            googlePlaceId: inheritedItem?.googlePlaceId || undefined,
            googlePlaceData: inheritedItem?.googlePlaceData || undefined,
            googleUrl: inheritedItem?.googleUrl || undefined,
            geoPoint: inheritedItem?.locationGeoPoint || undefined,
            headline: inheritedItem?.headline || undefined,
            benefits: inheritedItem?.benefits || undefined,
            bookingPolicy: inheritedItem?.bookingPolicy || undefined,
            albertineVerified: inheritedItem?.albertineVerified || undefined,
            recentlyOpened: inheritedItem?.recentlyOpened || undefined,
            eventDates: inheritedItem?.eventDates || undefined,
            travelItineraryNotes: inheritedItem?.travelItineraryNotes || undefined,
            discoverableForTenant: inheritedItem?.discoverableForTenant || undefined,
            suppliers: inheritedItem?.supplierLmtIds || undefined,
            translations: {
                de: defaultTranslationDe,
                fr: defaultTranslationFr,
            },
        },
    });
    const supplierOptions = listOfSuppliers.map((it) => ({
        id: it.id,
        text: it.name,
        selected: (inheritedItem?.supplierLmtIds || []).includes(it.id),
    }));
    const [loading, setLoading] = useState<boolean>(false);
    const { mutateAsync: generateCatalogItemContent, isPending: isGenerating } =
        usePost("generateCatalogItemContent");
    const [activeLanguage, setActiveLanguage] = useState<ServiceLanguage>("en");

    const [imageReferences, setImageReferences] = useState<
        ImageReference[] | undefined
    >(
        googlePlace
            ? placeImagesToImageReferences(googlePlace.photos || [])
            : itemImagesToImageReferences(catalogItem?.images || []),
    );
    const offeringCategories: OfferingCategory[] = getValues("offeringCategory")
        ? [getValues("offeringCategory")]
        : [];
    const tags = getValues("tags")! || [];
    const scrollElementRef = useRef<HTMLDivElement>(null);
    const linkedLocationRef = useRef<{
        clear: () => void | null;
    }>(null);
    const posthog = usePostHog();
    const { saveCatalogItem } = useSaveCatalogItem();
    const { mutateAsync: translateCatalogItemContent } = usePost(
        "translateCatalogItemContent",
    );

    const onSubmit: SubmitHandler<CatalogItemFormSchema> = async (update) => {
        try {
            setLoading(true);

            const editable = fromCatalogItemFormToEditables(update);

            if (editable.published && !hasTranslations("all", editable)) {
                EventEmitter.emit({
                    name: "showToastMessage",
                    params: {
                        type: "error",
                        message: "Translations are required for publishing an item",
                    },
                });
                return;
            }

            const savedItem = await saveCatalogItem({
                catalogItemId: catalogItem?.id,
                form: update,
                imageReferences: imageReferences ?? [],
            });
            onSave(savedItem);
        } catch (error) {
            Sentry.captureException(error, {
                extra: { message: "Failed to save catalog item" },
            });
            EventEmitter.emit({
                name: "showToastMessage",
                params: {
                    message: toApiErrorMessage(error, "Saving the catalog item failed"),
                    type: "error",
                },
            });
        } finally {
            setLoading(false);
        }
    };

    const onInvalid: SubmitErrorHandler<CatalogItemFormSchema> = () => {
        void trigger();
        scrollRefToTop(scrollElementRef, true);
    };

    const resetLinkedLocation = (place: GooglePlace) => {
        const updates = newCatalogItemPayloadFromGooglePlace(place);

        const websiteUpdate =
            updates.website && isEmpty(getValues("website"))
                ? updates.website
                : getValues("website");

        const phoneUpdate =
            updates.phoneNumber && isEmpty(getValues("phoneNumber"))
                ? updates.phoneNumber
                : getValues("phoneNumber");

        // merge update.tags with inherited tags to get new set of unique tags
        const allTags = [...(inheritedItem?.tags || []), ...(updates.tags || [])];
        const uniqueTags = removeDuplicates(allTags);

        reset({
            ...getValues(),
            addressComponents: updates.addressComponents ?? undefined,
            googlePlaceId: updates.googlePlaceId,
            googlePlaceData: place,
            geoPoint: updates.locationGeoPoint,
            googleUrl: updates.googleUrl,
            address: updates.address || "",
            website: websiteUpdate,
            phoneNumber: phoneUpdate,
            tags: uniqueTags,
        });
        void trigger();
    };

    const emptyLinkedLocation = () => {
        reset({
            ...getValues(),
            addressComponents: null,
            googlePlaceId: null,
            geoPoint: null,
            googleUrl: null,
        });
        void trigger();
    };

    const errorTexts = collectErrorMessages(errors);

    const addTranslation = async (language: AdditionalServiceLanguage) => {
        if (!isValid) {
            EventEmitter.emit({
                name: "showToastMessage",
                params: {
                    type: "error",
                    message: "Fix issues before adding translations",
                },
            });
            void handleSubmit(onSubmit, onInvalid)();
            return;
        }
        const contentToTranslate: CatalogItemEditableTranslation = {
            address: getValues("address") || null,
            benefits: getValues("benefits") || null,
            bookingPolicy: getValues("bookingPolicy") || null,
            description: getValues("description") || null,
            eventDates: getValues("eventDates") || null,
            headline: getValues("headline") || null,
            localInsights: getValues("localInsights") || null,
            name: getValues("name") || null,
            travelItineraryNotes: getValues("travelItineraryNotes") || null,
        };

        try {
            const { translation } = await translateCatalogItemContent({
                json: {
                    language,
                    content: contentToTranslate,
                },
            });

            reset({
                ...getValues(),
                translations: {
                    ...getValues("translations"),
                    [language]: translation,
                },
            });

            trigger();
        } catch (error) {
            Sentry.captureException(error, {
                extra: {
                    message: "Failed to translate catalog item content",
                    language,
                },
            });
            EventEmitter.emit({
                name: "showToastMessage",
                params: {
                    type: "error",
                    message: `Adding ${ServiceLanguageMap[language]} translation failed`,
                },
            });
        }
    };

    const handleGenerateContent = async () => {
        const name = getValues("name");
        const offeringCategory = getValues("offeringCategory");

        if (!name || !offeringCategory) {
            EventEmitter.emit({
                name: "showToastMessage",
                params: {
                    type: "error",
                    message: "Please fill in venue name and type first.",
                },
            });
            return;
        }

        const address = getValues("address") || null;
        const website = getValues("website");
        const websiteUrl = website ? website : null;

        let results: { description: string; localInsights: string } | null = null;
        try {
            results = await generateCatalogItemContent({
                json: {
                    name,
                    offeringCategory,
                    address,
                    websiteUrl,
                },
            });
        } catch (error) {
            posthog.captureException(error);
        }

        posthog.capture("AI Generate Venue Content", {
            venueName: name,
            venueType: offeringCategory,
            venueLocation: address,
            venueUrl: websiteUrl,
            resultDescription: results?.description,
            resultInsight: results?.localInsights,
            success: !!results,
        });

        if (!results) {
            EventEmitter.emit({
                name: "showToastMessage",
                params: {
                    type: "error",
                    message: "Failed to generate content.",
                },
            });

            return;
        }

        reset({
            ...getValues(),
            description: results.description,
            localInsights: results.localInsights,
        });
        void trigger(["description", "localInsights"]);
    };
    const recentlyOpened = isRecentlyOpened(getValues("recentlyOpened"));
    const recentlyOpenedToggle = (
        <Flex.Column gap="medium">
            <TitleValuePairs>
                <TitleValuePair
                    title={
                        <Label.Medium.SemiBold.N10>
                            Recently opened
                        </Label.Medium.SemiBold.N10>
                    }
                    value={
                        <Flex.Row gap="medium">
                            <Toggle
                                checked={recentlyOpened}
                                onChange={() => {
                                    const value = getValues("recentlyOpened");

                                    if (value) {
                                        setValue("recentlyOpened", undefined);
                                    } else {
                                        setValue(
                                            "recentlyOpened",
                                            new Date().toISOString(),
                                        );
                                    }
                                    void trigger(["recentlyOpened"]);
                                }}
                            />

                            {recentlyOpened ? (
                                <Tag.Medium.P90>Yes</Tag.Medium.P90>
                            ) : (
                                <Tag.Medium.N80>No</Tag.Medium.N80>
                            )}
                        </Flex.Row>
                    }
                />
            </TitleValuePairs>

            <Label.Medium.Light.N10>
                This will be automatically turned off after 3 months
            </Label.Medium.Light.N10>
        </Flex.Column>
    );

    const albertineVerifiedToggle = (
        <Flex.Column gap="medium">
            <TitleValuePairs>
                <TitleValuePair
                    title={
                        <Label.Medium.SemiBold.N10>
                            Albertine verified
                        </Label.Medium.SemiBold.N10>
                    }
                    value={
                        <Flex.Row gap="medium">
                            <Toggle
                                checked={!!getValues("albertineVerified")}
                                onChange={() => {
                                    const value = getValues("albertineVerified");

                                    if (value) {
                                        setValue("albertineVerified", undefined);
                                    } else {
                                        setValue(
                                            "albertineVerified",
                                            new Date().toISOString(),
                                        );
                                    }
                                    void trigger(["albertineVerified"]);
                                }}
                            />

                            {getValues("albertineVerified") ? (
                                <Tag.Medium.P90>Yes</Tag.Medium.P90>
                            ) : (
                                <Tag.Medium.N80>No</Tag.Medium.N80>
                            )}
                        </Flex.Row>
                    }
                />
            </TitleValuePairs>

            <Label.Medium.Light.N10>
                Verified articles are places we have personally visited or heard enough
                positive feedback about to recommend. Ideally, they should have a Google
                rating of 4.5 or higher.
            </Label.Medium.Light.N10>
        </Flex.Column>
    );

    const WOAPublishedToggle = (
        <Flex.Column gap="medium">
            <TitleValuePairs>
                <TitleValuePair
                    title={
                        <Label.Medium.SemiBold.N10>
                            Visibility
                        </Label.Medium.SemiBold.N10>
                    }
                    value={
                        <Flex.Row gap="medium">
                            {isAdmin && (
                                <Tooltip
                                    label="Please unarchive item first"
                                    hide={!catalogItem?.archived}
                                >
                                    <Toggle
                                        checked={getValues("published")}
                                        onChange={() => {
                                            setValue(
                                                "published",
                                                !getValues("published"),
                                            );
                                            void trigger(["published"]);
                                        }}
                                        disabled={catalogItem?.archived ?? false}
                                    />
                                </Tooltip>
                            )}

                            {getValues("published") ? (
                                <Tag.Medium.P90>Published</Tag.Medium.P90>
                            ) : (
                                <Tag.Medium.N80>Unpublished</Tag.Medium.N80>
                            )}
                        </Flex.Row>
                    }
                />
            </TitleValuePairs>

            <Label.Medium.Light.N10>
                {getValues("published")
                    ? "This item can be discovered or promoted in World of Albertine"
                    : "Unpublished articles can’t be discovered in the app’s World of Albertine section. The item can still be shared through the chat or in a request."}
            </Label.Medium.Light.N10>
        </Flex.Column>
    );

    const woaTenantSelect = getValues("published") && isAdmin && (
        <WOATenantSelect
            discoverableForTenant={inheritedItem?.discoverableForTenant}
            disabled={!isAdmin}
            onChange={(newValue) => {
                setValue("discoverableForTenant", newValue);
                void trigger(["discoverableForTenant"]);
            }}
        />
    );

    const englishBaseLineTab = (
        <Flex.Column gap="xlarge">
            <Flex.Column gap="medium">
                <Input.TextArea
                    className="textarea__heading catalog-item-form__name"
                    placeholder="Name"
                    value={defaultValues?.name || ""}
                    isValid={() => !errors.name}
                    rows={1}
                    maxLength={catalogItemNameMaxLength}
                    autoResize
                    trim
                    removeLineBreaks
                    onChange={(value) => {
                        setValue("name", value);
                        void trigger(["name"]);
                    }}
                />
            </Flex.Column>

            <ImageSelector
                id={`catalog-item-form__${
                    getValues("googlePlaceId") || catalogItem?.id
                }`}
                title="Images"
                subtitle="Upload or search from google and website images"
                googlePlaceId={getValues("googlePlaceId") || undefined}
                websiteUrl={getValues("website") || undefined}
                initialImageReferences={imageReferences}
                isValid={() => !errors.images}
                uploading={loading}
                onChange={(imageRefs) => {
                    const refs = imageRefs || [];
                    setImageReferences(refs);
                    setValue(
                        "images",
                        refs.map((it) => ({
                            id: getUrlFromReference(it),
                            url: getUrlFromReference(it),
                        })),
                    );
                    void trigger(["images"]);
                }}
            />

            <Flex.Column gap="large">
                <Flex.Column gap="large">
                    <h3>Visible to member</h3>

                    <Flex.Row gap="medium" alignItems="start">
                        <Flex.Column gap="medium">
                            <Dropdown
                                label="Category"
                                optionList={offeringCategoryOptions}
                                isInvalidInput={
                                    (getValues("offeringCategory") as string) ===
                                    OfferingCategoryUncategorizedKey
                                }
                                currentValue={
                                    getValues("offeringCategory") ||
                                    OfferingCategoryUncategorizedKey
                                }
                                onChange={(category) => {
                                    setValue(
                                        "offeringCategory",
                                        category as OfferingCategory,
                                    );
                                    void trigger(["offeringCategory"]);
                                }}
                            />
                            <Input.TextField
                                label="Website"
                                placeholder="https://"
                                value={defaultValues?.website || ""}
                                isValid={() => !errors.website}
                                onChange={(value) => {
                                    setValue(
                                        "website",
                                        value.length !== 0 ? value : undefined,
                                    );
                                    void trigger(["website"]);
                                }}
                            />

                            <Input.TextField
                                label="Phone"
                                placeholder="+1 123 456 7890"
                                value={defaultValues?.phoneNumber || ""}
                                isValid={() => !errors.phoneNumber}
                                onChange={(value) => {
                                    setValue("phoneNumber", value);
                                    void trigger(["phoneNumber"]);
                                }}
                            />
                        </Flex.Column>

                        {showAddress(offeringCategories) && (
                            <Flex.Column gap="medium">
                                <GooglePlaceInputField
                                    ref={linkedLocationRef}
                                    label="Linked location"
                                    googlePlaceId={
                                        inheritedItem?.googlePlaceData?.displayName
                                            ?.text ||
                                        inheritedItem?.googlePlaceId ||
                                        undefined
                                    }
                                    fields={GooglePlaceFieldsNewCatalogItem}
                                    onSelect={resetLinkedLocation}
                                    onClear={emptyLinkedLocation}
                                />

                                <Input.TextField
                                    label="Visible address"
                                    placeholder="Address"
                                    value={defaultValues?.address || ""}
                                    isValid={() => !errors.address}
                                    onChange={(value) => {
                                        setValue("address", value);
                                        void trigger(["address"]);
                                    }}
                                />
                            </Flex.Column>
                        )}
                    </Flex.Row>
                    {showEventDates(offeringCategories) && (
                        <Input.TextField
                            label="Event dates"
                            placeholder={`Use the international date format e.g. 1 Jan ${new Date().getFullYear()}`}
                            value={defaultValues?.eventDates || ""}
                            isValid={() => !errors.eventDates}
                            onChange={(value) => {
                                setValue("eventDates", value);
                                void trigger(["eventDates"]);
                            }}
                        />
                    )}
                    {showTravelItineraryNotes(offeringCategories) && (
                        <Input.TextField
                            label="Ideal trip time and duration"
                            placeholder="E.g. 1–2 weeks, best between Jun-Sep"
                            value={defaultValues?.travelItineraryNotes || ""}
                            isValid={() => !errors.travelItineraryNotes}
                            onChange={(value) => {
                                setValue("travelItineraryNotes", value);
                                void trigger(["travelItineraryNotes"]);
                            }}
                        />
                    )}
                </Flex.Column>

                <Flex.Column gap="medium">
                    <Flex.Row
                        gap="medium"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <h4>Description</h4>

                        {(getValues("offeringCategory") === "dining" ||
                            getValues("offeringCategory") === "nightlife") && (
                            <Button.P90
                                showLoadingSpinner={isGenerating}
                                onClick={() => void handleGenerateContent()}
                            >
                                <Icon.WandStars.Small.P10 />
                                Generate description & insight
                            </Button.P90>
                        )}
                    </Flex.Row>

                    <Input.TextField
                        label="Snappy headline"
                        placeholder="A 3–10 word title describing what’s cool or defining about this place"
                        value={defaultValues?.headline ?? ""}
                        isValid={() => !errors.headline}
                        onChange={(value) => {
                            setValue("headline", value);
                            void trigger(["headline"]);
                        }}
                    />

                    <TiptapEditor
                        disabled={isGenerating}
                        isValid={() => !errors.description}
                        content={defaultValues?.description ?? ""}
                        label="General description"
                        placeholder="One-to-three-paragraph description of what the place is like and who it is for"
                        onChange={(value) => {
                            setValue("description", value);
                            void trigger(["description"]);
                        }}
                    />

                    <Input.TextArea
                        label="Albertine insight"
                        placeholder="One paragraph insight with our unique point of view (e.g. “try this dish”, “we like this place because…”)"
                        value={defaultValues?.localInsights ?? ""}
                        isValid={() => !errors.localInsights}
                        autoResize
                        disabled={isGenerating}
                        onChange={(value) => {
                            setValue("localInsights", value);
                            void trigger(["localInsights"]);
                        }}
                    />
                    <Input.TextArea
                        label="Albertine benefit"
                        placeholder="Perks, benefits, or access provided by Albertine (“eg. early check-in subject to availability, “book with us for exclusive access”)"
                        value={defaultValues?.benefits || ""}
                        isValid={() => !errors.benefits}
                        autoResize
                        onChange={(value) => {
                            setValue("benefits", value);
                            void trigger(["benefits"]);
                        }}
                    />
                    <Input.TextArea
                        label="Booking policy"
                        placeholder="Is there a deposit? Can I cancel? Any other T&C’s the customer should know?"
                        value={defaultValues?.bookingPolicy || ""}
                        isValid={() => !errors.bookingPolicy}
                        autoResize
                        onChange={(value) => {
                            setValue("bookingPolicy", value);
                            void trigger(["bookingPolicy"]);
                        }}
                    />
                </Flex.Column>

                <Flex.Column gap="medium">
                    <h4>Tags</h4>
                    <SelectTagsWithCategory
                        key={JSON.stringify(tags) + offeringCategories[0]}
                        offeringCategory={offeringCategories[0]}
                        values={tags}
                        onChange={(value) => {
                            setValue("tags", value);
                            void trigger(["tags"]);
                        }}
                    />
                </Flex.Column>
            </Flex.Column>

            <Flex.Column gap="large">
                <h3>Not visible to member</h3>

                <Flex.Column gap="xlarge">
                    <Flex.Column gap="medium">
                        <Flex.Row gap="medium">
                            <Dropdown
                                label="Source:"
                                entries={Object.entries(CatalogSources)}
                                currentValue={getValues("source")}
                                onChange={(value) => {
                                    setValue("source", value as CatalogSourceKey);
                                    void trigger(["source"]);
                                }}
                            />
                            <AutocompleteDropdown
                                label="Suppliers"
                                items={supplierOptions}
                                onChange={(newSuppliers) => {
                                    setValue(
                                        "suppliers",
                                        newSuppliers.map((it) => it.id),
                                    );
                                    void trigger(["suppliers"]);
                                }}
                            />
                        </Flex.Row>

                        <Input.TextArea
                            label="Internal notes for Albertine"
                            placeholder="What should the Albertine internal team know about the place?"
                            value={defaultValues?.notes || ""}
                            isValid={() => !errors.notes}
                            autoResize
                            onChange={(value) => {
                                setValue("notes", value);
                                void trigger(["notes"]);
                            }}
                        />
                    </Flex.Column>

                    {showRecentlyOpened(offeringCategories) && recentlyOpenedToggle}
                    {albertineVerifiedToggle}
                </Flex.Column>
            </Flex.Column>
            <Flex.Column gap="large">
                <h3>World of Albertine</h3>
                {WOAPublishedToggle}
                {woaTenantSelect}
            </Flex.Column>
        </Flex.Column>
    );

    const germanTranslationTab = (
        <Flex.Column gap="xlarge">
            <Input.TextArea
                className="textarea__heading catalog-item-form__name"
                placeholder="Name"
                value={defaultValues?.translations?.de?.name || ""}
                isValid={() => !errors.translations?.de?.name}
                rows={1}
                maxLength={catalogItemNameMaxLength}
                autoResize
                trim
                removeLineBreaks
                onChange={(value) => {
                    setValue("translations.de.name", value);
                    void trigger(["translations.de.name"]);
                }}
                supportingText={getValues("name")}
            />

            <Flex.Column gap="xlarge">
                <Input.TextField
                    label="Snappy headline"
                    placeholder="A 3–10 word title describing what’s cool or defining about this place"
                    value={defaultValues?.translations?.de?.headline || ""}
                    isValid={() => !errors.translations?.de?.headline}
                    onChange={(value) => {
                        setValue("translations.de.headline", value);
                        void trigger(["translations.de.headline"]);
                    }}
                    supportingText={getValues("headline") || undefined}
                />
                {showEventDates(offeringCategories) && (
                    <Input.TextField
                        label="Event dates"
                        placeholder={`Use the international date format e.g. 1 Jan ${new Date().getFullYear()}`}
                        value={defaultValues?.translations?.de?.eventDates || ""}
                        isValid={() => !errors.translations?.de?.eventDates}
                        onChange={(value) => {
                            setValue("translations.de.eventDates", value);
                            void trigger(["translations.de.eventDates"]);
                        }}
                        supportingText={getValues("eventDates") || undefined}
                    />
                )}
                {showTravelItineraryNotes(offeringCategories) && (
                    <Input.TextField
                        label="Ideal trip time and duration"
                        placeholder="E.g. 1–2 weeks, best between Jun-Sep"
                        value={
                            defaultValues?.translations?.de?.travelItineraryNotes || ""
                        }
                        isValid={() => !errors.translations?.de?.travelItineraryNotes}
                        onChange={(value) => {
                            setValue("translations.de.travelItineraryNotes", value);
                            void trigger(["translations.de.travelItineraryNotes"]);
                        }}
                        supportingText={getValues("travelItineraryNotes") || undefined}
                    />
                )}

                <TiptapEditor
                    disabled={isGenerating}
                    content={defaultValues?.translations?.de?.description ?? ""}
                    isValid={() => !errors.translations?.de?.description}
                    label="General description"
                    placeholder="One-to-three-paragraph description of what the place is like and who it is for"
                    onChange={(value) => {
                        setValue("translations.de.description", value);
                        void trigger(["translations.de.description"]);
                    }}
                    supportingText={getValues("description")}
                />

                <Input.TextArea
                    label="Albertine insight"
                    placeholder="One paragraph insight with our unique point of view (e.g. “try this dish”, “we like this place because…”)"
                    value={defaultValues?.translations?.de?.localInsights || ""}
                    isValid={() => !errors.translations?.de?.localInsights}
                    autoResize
                    onChange={(value) => {
                        setValue("translations.de.localInsights", value);
                        void trigger(["translations.de.localInsights"]);
                    }}
                    supportingText={getValues("localInsights") || undefined}
                />
                <Input.TextArea
                    label="Albertine benefit"
                    placeholder="Perks, benefits, or access provided by Albertine (“eg. early check-in subject to availability, “book with us for exclusive access”)"
                    value={defaultValues?.translations?.de?.benefits || ""}
                    isValid={() => !errors.translations?.de?.benefits}
                    autoResize
                    onChange={(value) => {
                        setValue("translations.de.benefits", value);
                        void trigger(["translations.de.benefits"]);
                    }}
                    supportingText={getValues("benefits") || undefined}
                />
                <Input.TextArea
                    label="Booking policy"
                    placeholder="Is there a deposit? Can I cancel? Any other T&C’s the customer should know?"
                    value={defaultValues?.translations?.de?.bookingPolicy || ""}
                    isValid={() => !errors.translations?.de?.bookingPolicy}
                    autoResize
                    onChange={(value) => {
                        setValue("translations.de.bookingPolicy", value);
                        void trigger(["translations.de.bookingPolicy"]);
                    }}
                    supportingText={getValues("bookingPolicy") || undefined}
                />
            </Flex.Column>
        </Flex.Column>
    );

    const frenchTranslationTab = (
        <Flex.Column gap="xlarge">
            <Input.TextArea
                className="textarea__heading catalog-item-form__name"
                placeholder="Name"
                value={defaultValues?.translations?.fr?.name || ""}
                isValid={() => !errors.translations?.fr?.name}
                rows={1}
                maxLength={catalogItemNameMaxLength}
                autoResize
                trim
                removeLineBreaks
                onChange={(value) => {
                    setValue("translations.fr.name", value);
                    void trigger(["translations.fr.name"]);
                }}
                supportingText={getValues("name")}
            />

            <Flex.Column gap="xlarge">
                <Input.TextField
                    label="Snappy headline"
                    placeholder="A 3–10 word title describing what’s cool or defining about this place"
                    value={defaultValues?.translations?.fr?.headline || ""}
                    isValid={() => !errors.translations?.fr?.headline}
                    onChange={(value) => {
                        setValue("translations.fr.headline", value);
                        void trigger(["translations.fr.headline"]);
                    }}
                    supportingText={getValues("headline") || undefined}
                />
                {showEventDates(offeringCategories) && (
                    <Input.TextField
                        label="Event dates"
                        placeholder={`Use the international date format e.g. 1 Jan ${new Date().getFullYear()}`}
                        value={defaultValues?.translations?.fr?.eventDates || ""}
                        isValid={() => !errors.translations?.fr?.eventDates}
                        onChange={(value) => {
                            setValue("translations.fr.eventDates", value);
                            void trigger(["translations.fr.eventDates"]);
                        }}
                        supportingText={getValues("eventDates") || undefined}
                    />
                )}
                {showTravelItineraryNotes(offeringCategories) && (
                    <Input.TextField
                        label="Ideal trip time and duration"
                        placeholder="E.g. 1–2 weeks, best between Jun-Sep"
                        value={
                            defaultValues?.translations?.fr?.travelItineraryNotes || ""
                        }
                        isValid={() => !errors.translations?.fr?.travelItineraryNotes}
                        onChange={(value) => {
                            setValue("translations.fr.travelItineraryNotes", value);
                            void trigger(["translations.fr.travelItineraryNotes"]);
                        }}
                        supportingText={getValues("travelItineraryNotes") || undefined}
                    />
                )}

                <TiptapEditor
                    disabled={isGenerating}
                    content={defaultValues?.translations?.fr?.description ?? ""}
                    isValid={() => !errors.translations?.fr?.description}
                    label="General description"
                    placeholder="One-to-three-paragraph description of what the place is like and who it is for"
                    onChange={(value) => {
                        setValue("translations.fr.description", value);
                        void trigger(["translations.fr.description"]);
                    }}
                    supportingText={getValues("description")}
                />

                <Input.TextArea
                    label="Albertine insight"
                    placeholder="One paragraph insight with our unique point of view (e.g. “try this dish”, “we like this place because…”)"
                    value={defaultValues?.translations?.fr?.localInsights || ""}
                    isValid={() => !errors.translations?.fr?.localInsights}
                    autoResize
                    onChange={(value) => {
                        setValue("translations.fr.localInsights", value);
                        void trigger(["translations.fr.localInsights"]);
                    }}
                    supportingText={getValues("localInsights") || undefined}
                />
                <Input.TextArea
                    label="Albertine benefit"
                    placeholder="Perks, benefits, or access provided by Albertine (“eg. early check-in subject to availability, “book with us for exclusive access”)"
                    value={defaultValues?.translations?.fr?.benefits || ""}
                    isValid={() => !errors.translations?.fr?.benefits}
                    autoResize
                    onChange={(value) => {
                        setValue("translations.fr.benefits", value);
                        void trigger(["translations.fr.benefits"]);
                    }}
                    supportingText={getValues("benefits") || undefined}
                />
                <Input.TextArea
                    label="Booking policy"
                    placeholder="Is there a deposit? Can I cancel? Any other T&C’s the customer should know?"
                    value={defaultValues?.translations?.fr?.bookingPolicy || ""}
                    isValid={() => !errors.translations?.fr?.bookingPolicy}
                    autoResize
                    onChange={(value) => {
                        setValue("translations.fr.bookingPolicy", value);
                        void trigger(["translations.fr.bookingPolicy"]);
                    }}
                    supportingText={getValues("bookingPolicy") || undefined}
                />
            </Flex.Column>
        </Flex.Column>
    );

    const translationsInForm = {
        translations: {
            de: getValues("translations.de"),
            fr: getValues("translations.fr"),
        },
    };
    const hasTranslationsDe = Object.values(
        translationsInForm.translations.de || {},
    ).some(Boolean);
    const hasTranslationsFr = Object.values(
        translationsInForm.translations.fr || {},
    ).some(Boolean);
    const translations: Record<ServiceLanguage, React.ReactNode> = {
        en: englishBaseLineTab,
        de: hasTranslationsDe ? germanTranslationTab : undefined,
        fr: hasTranslationsFr ? frenchTranslationTab : undefined,
    };

    const addFrench: MenuButtonOptions = [
        {
            label: "French",
            icon: "Add",
            onClick: () => addTranslation("fr"),
        },
    ];
    const addGerman: MenuButtonOptions = [
        {
            label: "German",
            icon: "Add",
            onClick: () => addTranslation("de"),
        },
    ];

    return (
        <Flex.Row alignItems="start" className="catalog-item-form__container">
            <Flex.Column className="catalog-item-form__item">
                <Form.Base
                    className="catalog-item-form__form"
                    onSubmit={handleSubmit(onSubmit, onInvalid)}
                >
                    <Form.Inputs ref={scrollElementRef} disabled={loading}>
                        {!isValid && isSubmitted && <Form.Errors errors={errorTexts} />}

                        <TranslationTabs
                            keepAllTabsRendered
                            translationOptions={[
                                ...(!hasTranslationsFr ? addFrench : []),
                                ...(!hasTranslationsDe ? addGerman : []),
                            ]}
                            tabs={Object.entries(translations)
                                .filter(([_key, value]) => value)
                                .map(([key, value]) => ({
                                    key: key as ServiceLanguage, // TODO: Remove the need for setting type
                                    text:
                                        ServiceLanguageMap[key as ServiceLanguage] ||
                                        key,
                                    children: value,
                                }))}
                            onTabClick={setActiveLanguage}
                        />
                    </Form.Inputs>

                    <Form.Actions>
                        <div>
                            <Flex.Row gap="medium">
                                <Button onClick={onCancel}>Cancel</Button>
                                <Button.N10
                                    type="submit"
                                    disabled={loading}
                                    showLoadingSpinner={loading}
                                >
                                    Save
                                </Button.N10>
                            </Flex.Row>
                        </div>
                    </Form.Actions>
                </Form.Base>
            </Flex.Column>

            <Flex.Column
                alignItems="center"
                justifyContent="start"
                className="catalog-item-form__preview"
            >
                {activeLanguage === "en" && (
                    <AppPreview.WoaArticle
                        catalogItem={{
                            ...getValues(),
                            headline: getValues("headline"),
                            name: getValues("name"),
                            description: getValues("description"),
                            images: imageReferences?.map((it) => {
                                const url = getUrlFromReference(it);
                                return { id: url, url };
                            }),
                            offeringCategories,
                            localInsights: getValues("localInsights"),
                            source: getValues("source"),
                            notes: getValues("notes"),
                            address: getValues("address"),
                            phoneNumber: getValues("phoneNumber"),
                            website: getValues("website"),
                            keyDetail: inheritedItemKeyDetail,
                        }}
                    />
                )}

                {activeLanguage === "de" && (
                    <AppPreview.WoaArticle
                        catalogItem={{
                            ...getValues(),
                            headline: getValues("translations.de.headline") || "",
                            name: getValues("translations.de.name") || "",
                            description: getValues("translations.de.description"),
                            images: imageReferences?.map((it) => {
                                const url = getUrlFromReference(it);
                                return { id: url, url };
                            }),
                            offeringCategories,
                            localInsights: getValues("translations.de.localInsights"),
                            source: getValues("source"),
                            notes: getValues("notes"),
                            address: getValues("translations.de.address"),
                            phoneNumber: getValues("phoneNumber"),
                            website: getValues("website"),
                            keyDetail: inheritedItemKeyDetail,
                        }}
                    />
                )}

                {activeLanguage === "fr" && (
                    <AppPreview.WoaArticle
                        catalogItem={{
                            ...getValues(),
                            headline: getValues("translations.fr.headline") || "",
                            name: getValues("translations.fr.name") || "",
                            description: getValues("translations.fr.description"),
                            images: imageReferences?.map((it) => {
                                const url = getUrlFromReference(it);
                                return { id: url, url };
                            }),
                            offeringCategories,
                            localInsights: getValues("translations.fr.localInsights"),
                            source: getValues("source"),
                            notes: getValues("notes"),
                            address: getValues("translations.fr.address"),
                            phoneNumber: getValues("phoneNumber"),
                            website: getValues("website"),
                            keyDetail: inheritedItemKeyDetail,
                        }}
                    />
                )}
            </Flex.Column>
        </Flex.Row>
    );
}

type WOATenantSelectProps = {
    discoverableForTenant?: Tenant | null;
    disabled: boolean;
    onChange: (newValue: Tenant | null) => void;
};
function WOATenantSelect(props: WOATenantSelectProps) {
    const values: TagSelection<Tenant | "any">[] = useMemo(
        () => [
            {
                id: "any",
                value: "any",
                displayValue: "Any",
                selected: !props.discoverableForTenant,
            },
            ...TenantSchema.options.values().map((tenant) => ({
                id: tenant as Tenant,
                value: tenant,
                displayValue: formatTenantName(tenant),
                selected: props.discoverableForTenant === tenant,
            })),
        ],
        [props.discoverableForTenant],
    );

    return (
        <Flex.Column gap="medium">
            <TitleValuePairs>
                <TitleValuePair
                    title={
                        <Label.Medium.SemiBold.N10>Tenant</Label.Medium.SemiBold.N10>
                    }
                    value={
                        <SelectTags<Tenant | "any">
                            values={values}
                            onChange={(newValues) => {
                                const selectedValue = newValues.find(
                                    (it) => it.selected,
                                );
                                const selectedTenant = selectedValue?.id ?? "any";
                                props.onChange(
                                    selectedTenant === "any" ? null : selectedTenant,
                                );
                            }}
                            selectMultiple={false}
                            disabled={props.disabled}
                        />
                    }
                />
            </TitleValuePairs>

            <Label.Medium.Light.N10>
                {props.discoverableForTenant
                    ? `Only ${formatTenantName(props.discoverableForTenant)} members can find this item in the app Discover section.`
                    : "Any member can find this item in the app Discover section."}
            </Label.Medium.Light.N10>
        </Flex.Column>
    );
}

export default CatalogItemForm;
